
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import FormActions from "@/components/shared/FormActions";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/useAuth";
import { useNavigate } from "react-router-dom";
import { AddressFields } from "@/components/ui/address-fields";
import { useSupplierCategories } from "./hooks/useSupplierCategories";
import SupplierLogoUpload from "./SupplierLogoUpload";
import { PAYMENT_TERMS_PRESETS } from "./paymentTerms";

const supplierSchema = z.object({
  name: z.string().min(1, "Supplier name is required"),
  contact_name: z.string().optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: z.string().optional(),
  website: z.string().optional(),
  street_address: z.string().optional(),
  suburb: z.string().optional(),
  city: z.string().optional(),
  province_state: z.string().optional(),
  postal_code: z.string().optional(),
  default_minimum_order: z.string().optional(),
  category_id: z.string().optional(),
  trade_ids: z.array(z.string()).optional().default([]),
  tags: z.array(z.string()).optional().default([]),
  logo_url: z.string().optional().nullable(),
  payment_terms_preset: z.enum(["net_7","net_30","net_60","cod","other"]).nullable().optional(),
  payment_terms_custom: z.string().optional().nullable(),
}).refine(
  (v) => v.payment_terms_preset !== "other" || (v.payment_terms_custom?.trim().length ?? 0) > 0,
  { message: "Please describe the payment terms", path: ["payment_terms_custom"] },
);

type SupplierFormValues = z.infer<typeof supplierSchema>;

interface SupplierFormNewProps {
  supplierId?: string;
}

export default function SupplierFormNew({ supplierId }: SupplierFormNewProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newTag, setNewTag] = useState("");
  const notify = useNotify();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { categories, trades, isLoading: optionsLoading } = useSupplierCategories();

  const form = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: "",
      contact_name: "",
      email: "",
      phone: "",
      website: "",
      street_address: "",
      suburb: "",
      city: "",
      province_state: "",
      postal_code: "",
      default_minimum_order: "",
      category_id: "",
      trade_ids: [],
      tags: [],
      logo_url: null,
      payment_terms_preset: null,
      payment_terms_custom: "",
    },
  });

  useEffect(() => {
    if (supplierId) {
      setIsEditing(true);
      loadSupplier();
    }
  }, [supplierId]);

  const loadSupplier = async () => {
    if (!supplierId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("id", supplierId)
        .single();

      if (error) throw error;

      if (data) {
        // Parse metadata for tags if it exists
        let tags: string[] = [];
        if (data.metadata && typeof data.metadata === 'object' && data.metadata !== null) {
          const metadata = data.metadata as any;
          tags = metadata.tags || [];
        }

        // Reset form with fetched data
        form.reset({
          name: data.name || "",
          contact_name: data.contact_name || "",
          email: data.email || "",
          phone: data.phone || "",
          website: data.website || "",
          street_address: data.street_address || "",
          suburb: data.suburb || "",
          city: data.city || "",
          province_state: data.province_state || "",
          postal_code: data.postal_code || "",
          default_minimum_order: data.default_minimum_order?.toString() || "",
          category_id: data.category_id || "",
          trade_ids: data.trade_ids || [],
          tags: tags,
          logo_url: (data as any).logo_url ?? null,
          payment_terms_preset: (data as any).payment_terms_preset ?? null,
          payment_terms_custom: (data as any).payment_terms_custom ?? "",
        });
      }
    } catch (error: any) {
      console.error("Error loading supplier:", error);
      notify.error("Failed to load supplier information");
    } finally {
      setIsLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() === "") return;
    
    const currentTags = form.getValues("tags") || [];
    if (!currentTags.includes(newTag.trim())) {
      form.setValue("tags", [...currentTags, newTag.trim()]);
    }
    setNewTag("");
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues("tags") || [];
    form.setValue("tags", currentTags.filter(tag => tag !== tagToRemove));
  };

  const onSubmit = async (values: SupplierFormValues) => {
    if (!user?.id || !profile?.company_id) {
      notify.error("You must be logged in to create a supplier.");
      return;
    }

    setIsLoading(true);

    try {
      const supplierData = {
        name: values.name,
        contact_name: values.contact_name || null,
        email: values.email || null,
        phone: values.phone || null,
        website: values.website || null,
        street_address: values.street_address || null,
        suburb: values.suburb || null,
        city: values.city || null,
        province_state: values.province_state || null,
        postal_code: values.postal_code || null,
        default_minimum_order: values.default_minimum_order
          ? parseFloat(values.default_minimum_order)
          : null,
        category_id: values.category_id || null,
        trade_ids: values.trade_ids || [],
        logo_url: values.logo_url || null,
        payment_terms_preset: values.payment_terms_preset || null,
        payment_terms_custom:
          values.payment_terms_preset === "other"
            ? (values.payment_terms_custom?.trim() || null)
            : null,
        metadata: {
          tags: values.tags || []
        },
        company_id: profile.company_id,
        created_by: user.id,
      };

      let result;
      if (isEditing && supplierId) {
        // Update existing supplier
        result = await supabase
          .from("suppliers")
          .update(supplierData)
          .eq("id", supplierId);
      } else {
        // Create new supplier
        result = await supabase.from("suppliers").insert(supplierData);
      }

      if (result.error) throw result.error;

      notify.success(`Supplier ${isEditing ? "updated" : "created"} successfully`);
      navigate("/cost-center/suppliers");
    } catch (error: any) {
      console.error("Error saving supplier:", error);
      notify.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if ((isLoading && isEditing) || optionsLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading supplier information...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo</FormLabel>
                  <FormControl>
                    <SupplierLogoUpload
                      value={field.value ?? null}
                      onChange={(url) => field.onChange(url)}
                      companyId={profile?.company_id ?? ""}
                      supplierId={supplierId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Supplier Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter supplier name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="contact_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Primary contact person" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="contact@supplier.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="Phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://supplier.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Classification</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name} {category.is_default && "(Default)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="default_minimum_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Minimum Order Amount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="trade_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trades</FormLabel>
                  <div className="space-y-2">
                    <Select 
                      onValueChange={(value) => {
                        const currentTrades = field.value || [];
                        if (!currentTrades.includes(value)) {
                          field.onChange([...currentTrades, value]);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trades" />
                      </SelectTrigger>
                      <SelectContent>
                        {trades.map((trade) => (
                          <SelectItem key={trade.id} value={trade.id}>
                            {trade.name} {trade.is_default && "(Default)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {(field.value || []).map((tradeId) => {
                        const trade = trades.find(t => t.id === tradeId);
                        return trade ? (
                          <Badge key={trade.id} variant="secondary">
                            {trade.name}
                            <button
                              type="button"
                              onClick={() => {
                                const updatedTrades = (field.value || []).filter(id => id !== tradeId);
                                field.onChange(updatedTrades);
                              }}
                              className="ml-1 hover:bg-destructive/20 rounded-full"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a tag"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                      />
                      <Button type="button" onClick={addTag}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(field.value || []).map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:bg-destructive/20 rounded-full"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <FormField
                control={form.control}
                name="payment_terms_preset"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Terms</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === "__none" ? null : v)}
                      value={field.value ?? "__none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment terms" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none">Not set</SelectItem>
                        {PAYMENT_TERMS_PRESETS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch("payment_terms_preset") === "other" ? (
                <FormField
                  control={form.control}
                  name="payment_terms_custom"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Describe Terms</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 2/10 Net 30" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Address</h3>
              <AddressFields form={form} />
            </div>

            <FormActions
              onCancel={() => navigate("/cost-center/suppliers")}
              isSubmitting={isLoading}
              submitLabel={isEditing ? "Update Supplier" : "Create Supplier"}
              submittingLabel="Saving..."
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
